/**
 * Options for creating a {@link PointerLockManager}.
 */
export interface PointerLockManagerOptions {
    /** Called when the browser exits pointer lock externally
     *  (user pressed Esc or switched tab/window, or the locked
     *  element left the page). */
    onExternalExit: () => void;
    /**
     * Called when requestPointerLock() is denied (e.g. browser
     * cooldown after ESC). The mouse gesture can continue
     * without pointer lock; the consumer should restore cursor
     * visibility as a fallback.
     */
    onLockDenied?: () => void;
}

/**
 * Encapsulates the full Pointer Lock API lifecycle so that
 * consumers don't have to manage listeners, cross-browser
 * Promise/void return differences, or late-grant races.
 *
 * Typical usage:
 * ```
 * const plm = new PointerLockManager({ onExternalExit: () => cancel() });
 * // on mousedown (user gesture):
 * plm.setButtonHeld(true);
 * plm.requestLock(canvas);
 * // when operation activates:
 * plm.setActive(true);
 * // when done:
 * plm.setActive(false);
 * plm.setButtonHeld(false);
 * plm.releaseLock();
 * ```
 *
 * Note: Pointer Lock is not guaranteed — the browser may deny the request
 * (e.g. if the user recently exited pointer lock and the browser is enforcing
 * a cooldown). Consumers should handle this gracefully via the `onLockDenied` callback.
 */
export class PointerLockManager {
    private readonly onExternalExit: () => void;
    private readonly onLockDenied: (() => void) | undefined;

    /** Element that pointer lock is requested/held on. */
    private target: HTMLElement | undefined;

    /** Bound handler reference for add/removeEventListener. */
    private boundOnPointerLockChange: (() => void) | undefined;

    /** Whether the consumer's operation is active. */
    private isActive = false;

    /** Whether the trigger button is currently held. */
    private isButtonHeld = false;

    /**
     * Reference to the one-shot safety handler installed by
     * `forceCleanUp()`. Stored so that `requestLock()` can
     * cancel it before starting a new cycle — otherwise the
     * safety handler could release a legitimately acquired lock
     * and trigger a spurious `onExternalExit`.
     */
    private safetyHandler: (() => void) | undefined;

    /**
     * True when we have called `exitPointerLock()` (either
     * from `releaseLock()` or from the late-grant branch) and
     * the resulting async `pointerlockchange` (release) has
     * not yet fired. This prevents a race where a new
     * `requestLock()` cycle starts before that event arrives,
     * which would otherwise be misidentified as an external
     * exit.
     *
     * Also cleared in the `requestLock()` catch handler: if
     * the listener is removed due to a denied request while a
     * release event is still pending, the event will never be
     * processed, so the flag must be reset to avoid stale
     * state leaking into the next lock cycle.
     */
    private expectingProgrammaticRelease = false;

    constructor(options: PointerLockManagerOptions) {
        this.onExternalExit = options.onExternalExit;
        this.onLockDenied = options.onLockDenied;
    }

    // ───────── public API ─────────

    /** True when pointer lock is held on our target. */
    public get isLocked(): boolean {
        return (
            this.target !== undefined &&
            document.pointerLockElement === this.target
        );
    }

    /**
     * Tell the manager whether the consumer's operation is
     * active (e.g. windowing has started, threshold exceeded).
     */
    public setActive(active: boolean): void {
        this.isActive = active;
    }

    /**
     * Tell the manager whether the trigger button is held.
     */
    public setButtonHeld(held: boolean): void {
        this.isButtonHeld = held;
    }

    /**
     * Request pointer lock on the given target element. Must be
     * called from a mousedown handler (user-gesture requirement).
     *
     * Attaches the `pointerlockchange` listener before requesting
     * so the grant event is never missed.
     */
    public requestLock(target: HTMLElement): void {
        this.target = target;

        // Cancel any lingering safety handler from a prior
        // forceCleanUp(). If left alive it would release the
        // lock we are about to acquire and cause a spurious
        // onExternalExit.
        this.cancelSafetyHandler();

        // Attach the listener BEFORE requesting so we never
        // miss the grant event.
        if (!this.boundOnPointerLockChange) {
            this.boundOnPointerLockChange = this.onPointerLockChange.bind(this);
            document.addEventListener(
                'pointerlockchange',
                this.boundOnPointerLockChange
            );
        }

        // requestPointerLock() returns void in older browsers
        // and a Promise in newer ones. Catch the Promise (if
        // any) to suppress unhandled-rejection warnings when
        // the browser denies the request.
        try {
            const result = target.requestPointerLock() as Promise<void> | void;
            if (result && typeof result.catch === 'function') {
                result.catch((e) => {
                    console.warn('Pointer lock request denied:', e);
                    // Clear stale programmatic-release flag: if a
                    // previous exitPointerLock() release event is
                    // still pending, removing the listener below
                    // means it will never be processed, so the
                    // flag must be reset to prevent future external
                    // exits from being misidentified as programmatic.
                    this.expectingProgrammaticRelease = false;
                    // pointerlockchange won't fire for a denied
                    // request — remove the listener now to avoid
                    // a permanent leak.
                    this.removeChangeListener();
                    // Notify consumer so it can restore cursor
                    // visibility as a graceful fallback. Called
                    // after cleanup so that if the callback
                    // synchronously starts a new lock cycle, our
                    // cleanup doesn't tear it down.
                    this.onLockDenied?.();
                });
            }
        } catch (e) {
            console.warn('Pointer lock request failed:', e);
        }
    }

    /**
     * Programmatic release. The actual release is async — the
     * `pointerlockchange` listener fires when it completes and
     * self-cleans. No-op if pointer lock is not currently held.
     */
    public releaseLock(): void {
        if (document.pointerLockElement) {
            this.expectingProgrammaticRelease = true;
            document.exitPointerLock();
        }
    }

    /**
     * Hard cleanup: release lock + remove listener immediately.
     * Use at init/teardown to recover from any leftover state.
     *
     * Installs a one-shot safety listener to catch a late grant
     * that may arrive after the main listener has been removed.
     * Without this, a pending `requestPointerLock()` that
     * resolves after cleanup would leave the cursor stuck in
     * pointer lock with no handler to release it. The listener
     * releases only a lock on the element this manager requested:
     * a lock on any other element belongs to another consumer. A
     * cleanup with no request since the previous one keeps the
     * listener that the previous one installed.
     */
    public forceCleanUp(): void {
        const requestedTarget = this.target;
        this.releaseLock();
        this.expectingProgrammaticRelease = false;
        this.removeChangeListener();
        this.isActive = false;
        this.isButtonHeld = false;
        this.target = undefined;

        if (requestedTarget === undefined) {
            return;
        }

        // Safety net: if a pending lock grant arrives after
        // cleanup, release it immediately so the cursor doesn't
        // get stuck in pointer lock with no handler.
        this.cancelSafetyHandler();
        this.safetyHandler = () => {
            if (document.pointerLockElement === requestedTarget) {
                document.exitPointerLock();
            }
            this.safetyHandler = undefined;
        };
        document.addEventListener('pointerlockchange', this.safetyHandler, {
            once: true,
        });
    }

    // ───────── internal ─────────

    /** Remove the `pointerlockchange` listener if one is attached. */
    private removeChangeListener(): void {
        if (this.boundOnPointerLockChange) {
            document.removeEventListener(
                'pointerlockchange',
                this.boundOnPointerLockChange
            );
            this.boundOnPointerLockChange = undefined;
        }
    }

    /** Remove the one-shot safety handler if one is pending. */
    private cancelSafetyHandler(): void {
        if (this.safetyHandler) {
            document.removeEventListener(
                'pointerlockchange',
                this.safetyHandler
            );
            this.safetyHandler = undefined;
        }
    }

    /**
     * `pointerlockchange` handler.
     *
     * When the lock is **acquired**:
     * - If neither `isButtonHeld` nor `isActive` → late grant after
     *   cleanup → set `expectingProgrammaticRelease` and exit
     *   immediately to avoid a stuck cursor.
     *
     * When the lock is **released**:
     * - If `expectingProgrammaticRelease` → this release is from
     *   our own `exitPointerLock()` call (via `releaseLock()` or
     *   the late-grant branch above). Clear the flag
     *   and self-clean the listener only if no new cycle is in
     *   progress (`isButtonHeld`/`isActive` are still false).
     * - Otherwise this is an external release. Self-clean the
     *   listener first to prevent re-entrancy, then call
     *   `onExternalExit` if `isActive` or `isButtonHeld`.
     */
    private onPointerLockChange(): void {
        if (document.pointerLockElement !== null) {
            // Lock acquired
            if (!this.isButtonHeld && !this.isActive) {
                // Late grant after full cleanup — exit right
                // away to avoid a stuck cursor.
                this.expectingProgrammaticRelease = true;
                document.exitPointerLock();
                // Don't remove the listener here — the
                // exitPointerLock() call above will fire another
                // pointerlockchange event asynchronously. The
                // flag above tells the "lock released" branch
                // to treat it as a programmatic release, not an
                // external exit.
            }
            return;
        }

        // Lock released — check if this is the async result of
        // our own late-grant exitPointerLock() call.
        if (this.expectingProgrammaticRelease) {
            this.expectingProgrammaticRelease = false;
            // Only self-clean if no new cycle is in progress.
            // A new requestLock() reuses the existing listener,
            // so removing it here would cause the new grant to
            // be missed.
            if (!this.isButtonHeld && !this.isActive) {
                this.removeChangeListener();
            }
            return;
        }

        // External release — snapshot callback/state before
        // cleaning up, because onExternalExit may synchronously
        // call releaseLock() or forceCleanUp(), which would null
        // out boundOnPointerLockChange or re-enter this handler.
        const shouldNotifyExternalExit = this.isActive || this.isButtonHeld;

        // Self-clean FIRST: remove the listener before calling
        // the consumer callback to prevent re-entrancy if the
        // callback triggers another lock request cycle.
        this.removeChangeListener();

        if (shouldNotifyExternalExit) {
            this.onExternalExit();
        }
    }
}
